/* ═══════════════════════════════════════════════════════════════════════════════
   TourWelcome: On-Brand "What's New" Modal Card
   
   Clean centered modal using the organization's brand colors.
   Shows feature highlights with staggered animations.
   No version numbers, no dark theme, it inherits the app's light design system.
   ═══════════════════════════════════════════════════════════════════════════════ */

import { motion } from 'framer-motion';
import type { TourDefinition } from '../../lib/tours/types';

interface TourWelcomeProps {
  tour: TourDefinition;
  onNext: () => void;
  onSkip: () => void;
}


const listItem = {
  hidden: { opacity: 0, x: -12 },
  show: { opacity: 1, x: 0, transition: { duration: 0.3, ease: [0.4, 0, 0.2, 1] as const } },
};

const container = {
  hidden: { opacity: 0 },
  show: { opacity: 1, transition: { staggerChildren: 0.08, delayChildren: 0.25 } },
};

export function TourWelcome({ tour, onNext, onSkip }: TourWelcomeProps) {
  // Derive feature highlights from the tour's spotlight steps (skip welcome/completion)
  const features = tour.steps
    .filter((s) => s.type === 'spotlight')
    .slice(0, 3)
    .map((s) => ({
      icon: s.icon || '✨',
      label: s.title,
      desc: s.description.length > 50 ? s.description.slice(0, 47) + '...' : s.description,
    }));

  return (
    <motion.div
      className="tour-welcome-backdrop"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.25 }}
    >
      <motion.div
        className="tour-welcome-modal"
        initial={{ opacity: 0, y: 20, scale: 0.97 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        exit={{ opacity: 0, y: -8, scale: 0.98 }}
        transition={{ type: 'spring', stiffness: 400, damping: 32, delay: 0.08 }}
      >


        {/* Content wrapper for padding */}
        <div className="tour-welcome-body">
          {/* Badge */}
          <motion.div
            className="tour-welcome-badge"
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.2, duration: 0.25 }}
          >
            {tour.steps[0]?.icon || '✨'} {tour.steps[0]?.title || 'What\'s New'}
          </motion.div>

          {/* Title */}
          <motion.h2
            className="tour-welcome-title"
            initial={{ opacity: 0, y: 6 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3, duration: 0.35 }}
          >
            {tour.title}
          </motion.h2>

          {/* Subtitle */}
          <motion.p
            className="tour-welcome-subtitle"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.4, duration: 0.35 }}
          >
            {tour.subtitle}
          </motion.p>

          {/* Feature list */}
          <motion.div
            className="tour-welcome-features"
            variants={container}
            initial="hidden"
            animate="show"
          >
            {features.map((f) => (
              <motion.div
                key={f.label}
                className="tour-welcome-feature"
                variants={listItem}
              >
                <span className="tour-welcome-feature-icon">{f.icon}</span>
                <div className="tour-welcome-feature-text">
                  <span className="tour-welcome-feature-label">{f.label}</span>
                  <span className="tour-welcome-feature-desc">{f.desc}</span>
                </div>
              </motion.div>
            ))}
          </motion.div>

          {/* Actions */}
          <motion.div
            className="tour-welcome-actions"
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.6, duration: 0.35 }}
          >
            <button className="tour-btn-primary tour-btn-pulse" onClick={onNext}>
              {tour.steps[0]?.actionLabel || 'Show Me →'}
            </button>
            <button className="tour-btn-skip" onClick={onSkip}>
              Maybe later
            </button>
          </motion.div>
        </div>
      </motion.div>
    </motion.div>
  );
}
